// This bridge exposes received WebRTC tracks only in the verified Puppeteer bot.
// Recording, encoding and uploads belong to the injected server recorder.
let verifiedBot = false;
let roomId = null;
let selfSocketId = null;
let joinedAtMs = null;
let participants = [];
let ended = false;
let publicBridge = null;
const remoteStreams = new Map();
const subscribers = new Set();
const firstSeenByEmail = new Map();
const firstSeenBySocket = new Map();

const now = () => performance.now();

export const isVerifiedRecordingBot = () =>
  verifiedBot && window.__iconnectParticipantRecorderInstalled === true;

const getSnapshot = () => {
  const humanSockets = new Set(
    participants
      .filter((participant) => participant.isBot === false && participant.socketId !== selfSocketId)
      .map((participant) => participant.socketId)
  );
  return {
    roomId,
    selfSocketId,
    joinedAtMs,
    nowMs: now(),
    participants: participants.map((participant) => ({ ...participant })),
    streams: [...remoteStreams.entries()]
      .filter(([socketId]) => humanSockets.has(socketId))
      .map(([socketId, entry]) => ({ socketId, stream: entry.stream })),
    ended,
  };
};

const publish = (observedAtMs = now()) => {
  if (!publicBridge) return;
  const snapshot = { ...getSnapshot(), nowMs: observedAtMs };
  subscribers.forEach((listener) => {
    try {
      listener(snapshot);
    } catch (error) {
      console.error("Bot audio bridge subscriber failed", error);
    }
  });
};

export const configureBotAudioBridge = ({ isBot, callId }) => {
  verifiedBot = isBot === true;
  roomId = callId || roomId;
  if (!isVerifiedRecordingBot() || publicBridge) return;
  publicBridge = Object.freeze({
    getSnapshot,
    subscribe(listener) {
      if (typeof listener !== "function") throw new TypeError("A bridge listener is required");
      subscribers.add(listener);
      listener(getSnapshot());
      return () => subscribers.delete(listener);
    },
  });
  window.__iconnectBotAudioBridge = publicBridge;
  window.dispatchEvent(new Event("iconnect:bot-audio-ready"));
};

export const observeBotAudioRoster = ({ callId, socketId, connectedUsers }) => {
  const observedAtMs = now();
  roomId = callId || roomId;
  selfSocketId = socketId || selfSocketId;
  participants = Array.isArray(connectedUsers)
    ? connectedUsers.map((participant) => ({ ...participant }))
    : [];
  // Record the first confirmed membership observation even if exposure is enabled later.
  if (joinedAtMs === null && participants.some((p) => p.socketId === selfSocketId && p.isBot === true)) {
    joinedAtMs = observedAtMs;
  }
  if (joinedAtMs !== null) {
    participants = participants.map((participant) => {
      if (participant.isBot !== false) return participant;
      const email = (participant.userEmail || "").trim();
      const firstSeenAtMs = firstSeenByEmail.get(email) ??
        firstSeenBySocket.get(participant.socketId) ?? observedAtMs;
      if (email) firstSeenByEmail.set(email, firstSeenAtMs);
      firstSeenBySocket.set(participant.socketId, firstSeenAtMs);
      return { ...participant, firstSeenAtMs };
    });
  }
  publish(observedAtMs);
};

export const attachBotAudioStream = (socketId, stream, generation) => {
  if (!isVerifiedRecordingBot() || ended || !stream || socketId === selfSocketId) return;
  const existing = remoteStreams.get(socketId);
  if (existing?.stream === stream && existing.generation === generation) {
    existing.refreshTracks();
    publish();
    return;
  }
  if (existing) existing.cleanup();
  const trackListeners = new Map();
  const refreshTracks = () => {
    const currentTracks = new Set(stream.getAudioTracks());
    trackListeners.forEach((listener, track) => {
      if (currentTracks.has(track)) return;
      ["mute", "unmute", "ended"].forEach((event) => track.removeEventListener(event, listener));
      trackListeners.delete(track);
    });
    currentTracks.forEach((track) => {
      if (trackListeners.has(track)) return;
      const listener = () => {
        if (remoteStreams.get(socketId)?.generation === generation) publish();
      };
      ["mute", "unmute", "ended"].forEach((event) => track.addEventListener(event, listener));
      trackListeners.set(track, listener);
    });
  };
  const onTracksChanged = () => {
    if (remoteStreams.get(socketId)?.generation !== generation) return;
    refreshTracks();
    publish();
  };
  const cleanup = () => {
    stream.removeEventListener("addtrack", onTracksChanged);
    stream.removeEventListener("removetrack", onTracksChanged);
    trackListeners.forEach((listener, track) => {
      ["mute", "unmute", "ended"].forEach((event) => track.removeEventListener(event, listener));
    });
    trackListeners.clear();
  };
  remoteStreams.set(socketId, { stream, generation, refreshTracks, cleanup });
  stream.addEventListener("addtrack", onTracksChanged);
  stream.addEventListener("removetrack", onTracksChanged);
  refreshTracks();
  publish();
};

export const detachBotAudioStream = (socketId, generation) => {
  const entry = remoteStreams.get(socketId);
  if (!entry || entry.generation !== generation) return;
  entry.cleanup();
  remoteStreams.delete(socketId);
  publish();
};

export const markBotAudioCallEnded = () => {
  if (ended) return;
  ended = true;
  // Keep tracks and the page alive until the server has drained its recorder.
  publish();
};
